import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { SvgBase } from './Icons';
import { sharePhoto } from 'common/utils/sharePhoto';
import { savePhoto } from 'common/utils/savePhoto';
import { useAlert } from 'contexts/AlertContext';
import styles from './PhotoViewer.module.css';

/* 좌우 화살표·닫기 — 이모지 대신 인라인 SVG */
const IcPrev = () => (
    <SvgBase size={18} strokeWidth={2}><path d="M15 5l-7 7 7 7" /></SvgBase>
);
const IcNext = () => (
    <SvgBase size={18} strokeWidth={2}><path d="M9 5l7 7-7 7" /></SvgBase>
);
const IcClose = () => (
    /* 48px 버튼 안에 20px 아이콘이라 사진 위에서 안 보였습니다. 표시를 키웁니다 */
    <SvgBase size={26} strokeWidth={2.4}><path d="M18 6L6 18M6 6l12 12" /></SvgBase>
);
/* 저장 — 네모로 화살표가 내려오는 모양 */
const IcDownload = () => (
    <SvgBase size={25} strokeWidth={2.4}>
        <path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7" />
        <path d="M12 3v12" />
        <path d="M8 11l4 4 4-4" />
    </SvgBase>
);

/* 공유 — 네모에서 화살표가 나가는 모양 */
const IcShare = () => (
    <SvgBase size={25} strokeWidth={2.4}>
        <path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7" />
        <path d="M12 15V3" />
        <path d="M8 7l4-4 4 4" />
    </SvgBase>
);

/** 좌우로 넘겨 보는 사진 묶음. 손가락 스와이프와 화살표 둘 다 됩니다. */
export const PhotoCarousel = ({
    photos, alt, className, onSelect, initialIndex = 0, onIndexChange,
}: {
    photos: string[];
    alt?: string;
    className?: string;
    /** 사진을 눌렀을 때 — 크게 보기로 이어집니다 */
    onSelect?: (index: number) => void;
    /** 몇 번째 장부터 보여줄지. 목록에서 고른 사진을 그대로 띄우기 위한 것입니다 */
    initialIndex?: number;
    /** 넘길 때마다 — 바깥에서 "지금 이 사진" 을 알아야 공유할 수 있습니다 */
    onIndexChange?: (index: number) => void;
}) => {
    const { t } = useTranslation();
    const [index, setIndex] = useState(initialIndex);
    const startX = useRef<number | null>(null);
    // 다른 장소를 고르면 첫 장부터 보여줍니다
    const key = photos.join('|');
    useEffect(() => { setIndex(initialIndex); }, [key, initialIndex]);
    useEffect(() => { onIndexChange?.(Math.min(index, photos.length - 1)); },
        // onIndexChange 를 의존성에 넣으면 부모가 새로 만들 때마다 돕니다
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [index, key]);

    if (photos.length === 0) return null;
    const idx = Math.min(index, photos.length - 1);
    const go = (step: number) => setIndex(i => (i + step + photos.length) % photos.length);

    return (
        <div
            className={className ? `${styles.carousel} ${className}` : styles.carousel}
            data-drag-lock=""
            onTouchStart={(e) => { startX.current = e.touches[0].clientX; }}
            onTouchEnd={(e) => {
                const from = startX.current;
                startX.current = null;
                if (from === null) return;
                const dx = e.changedTouches[0].clientX - from;
                // 40px 넘게 밀었을 때만 넘깁니다 (탭과 구분)
                if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
            }}
        >
            <img
                src={photos[idx]}
                alt={alt || ''}
                onClick={onSelect ? () => onSelect(idx) : undefined}
                style={onSelect ? { cursor: 'pointer' } : undefined}
            />
            {photos.length > 1 && (
                <>
                    <button
                        type="button"
                        className={`${styles.nav} ${styles.navPrev}`}
                        onClick={(e) => { e.stopPropagation(); go(-1); }}
                        aria-label={t('placeExplore.prevPhoto')}
                    ><IcPrev /></button>
                    <button
                        type="button"
                        className={`${styles.nav} ${styles.navNext}`}
                        onClick={(e) => { e.stopPropagation(); go(1); }}
                        aria-label={t('placeExplore.nextPhoto')}
                    ><IcNext /></button>
                    <div className={styles.dots}>
                        {photos.map((p, i) => (
                            <span key={p + i} className={i === idx ? `${styles.dot} ${styles.dotActive}` : styles.dot} />
                        ))}
                    </div>
                    <div className={styles.counter}>{idx + 1} / {photos.length}</div>
                </>
            )}
        </div>
    );
};

/** 사진을 화면 가득 띄우는 팝업. 뒤 배경이나 닫기 버튼으로 닫습니다. */
export const PhotoViewerModal = ({
    open, photos, alt, onClose, index = 0, shareTitle,
}: {
    open: boolean;
    photos: string[];
    alt?: string;
    onClose: () => void;
    /** 목록에서 고른 장수. 없으면 첫 장부터 */
    index?: number;
    /** 공유 시트에 함께 보낼 말 */
    shareTitle?: string;
}) => {
    const { t } = useTranslation();
    const { showAlert } = useAlert();
    /* 지금 몇 번째를 보고 있는지 — 공유·저장은 "보고 있는 그 사진" 이어야 합니다 */
    const [cur, setCur] = useState(index);
    const [saving, setSaving] = useState(false);
    const [sharing, setSharing] = useState(false);
    useEffect(() => { setCur(index); }, [index, open]);

    // 팝업이 떠 있는 동안 뒤 화면이 스크롤되지 않게 합니다
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = prev;
            window.removeEventListener('keydown', onKey);
        };
    }, [open, onClose]);

    if (!open || photos.length === 0) return null;
    const host = document.getElementById('portal') || document.body;

    return ReactDOM.createPortal(
        <div className={styles.scrim} onClick={onClose} role="dialog" aria-modal="true">
            <button
                type="button"
                className={styles.close}
                onClick={(e) => { e.stopPropagation(); onClose(); }}
                aria-label={t('common.close')}
            ><IcClose /></button>
            {/* 공유·저장 — 보고 있는 그 사진에 대해서 합니다 */}
            <div className={styles.actions} onClick={(e) => e.stopPropagation()}>
                <button
                    type="button"
                    className={styles.actionBtn}
                    disabled={sharing}
                    onClick={async () => {
                        if (sharing) return;
                        setSharing(true);
                        try {
                            const r = await sharePhoto(photos[Math.min(cur, photos.length - 1)], shareTitle);
                            /* 구버전 앱에서는 예전에 주소가 나갔습니다. 이제 보내지 않고
                               이유를 말합니다 — 링크를 받는 쪽에 보이면 안 됩니다. */
                            if (r === 'needsAppUpdate') showAlert({ message: t('common.photoShareNeedsUpdate') });
                            else if (r === 'copied') showAlert({ message: t('common.photoShareCopied') });
                            else if (r === 'failed') showAlert({ message: t('common.photoShareFailed') });
                        } finally {
                            setSharing(false);
                        }
                    }}
                    aria-label={t('common.share')}
                ><IcShare /></button>
                <button
                    type="button"
                    className={styles.actionBtn}
                    disabled={saving}
                    onClick={async () => {
                        if (saving) return;
                        setSaving(true);
                        try {
                            const r = await savePhoto(photos[Math.min(cur, photos.length - 1)]);
                            /* 결과를 반드시 말해 줍니다 — 누르고 아무 일도 없으면
                               고장으로 보입니다. 브라우저는 새 탭이 열리므로 조용히. */
                            if (r === 'saved') showAlert({ message: t('common.photoSaved') });
                            else if (r === 'needsAppUpdate') showAlert({ message: t('common.photoSaveNeedsUpdate') });
                            else if (r === 'failed') showAlert({ message: t('common.photoSaveFailed') });
                        } finally {
                            setSaving(false);
                        }
                    }}
                    aria-label={t('common.save')}
                ><IcDownload /></button>
            </div>
            <div className={styles.viewer} onClick={(e) => e.stopPropagation()}>
                <PhotoCarousel
                    photos={photos}
                    alt={alt}
                    initialIndex={index}
                    onIndexChange={setCur}
                />
            </div>
        </div>,
        host,
    );
};
